import type { QueryClient } from '@tanstack/react-query';
import { syncMentionTagLabelInCache } from '@/hooks/api/useMentionableResources';
import {
  syncProjectElementTagToResourceLibraryCaches,
  type ProjectElementType,
} from '@/hooks/api/useResourceLibrary';
import { storyboardBoardKeys } from '@/hooks/api/useStoryboardBoard';
import {
  storyboardDialoguesKeys,
  type StoryboardDialogueItem,
} from '@/hooks/api/useStoryboardDialogues';
import type {
  StoryboardBoardItem,
  StoryboardBoardPaginatedData,
  StoryboardBoardTrackWithItems,
  StoryboardItem,
} from '@/types';
import type { ElementType, ProjectElementTagResponse } from '@/types/project-element-tag';

const STORYBOARD_ITEMS_ROOT = 'storyboard-items';

type StoryboardBoardCacheData = StoryboardBoardTrackWithItems[] | StoryboardBoardPaginatedData;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function mapKeepingArrayReference<T>(items: T[], updater: (item: T) => T): T[] {
  let nextItems: T[] | null = null;

  for (let index = 0; index < items.length; index += 1) {
    const current = items[index];
    const next = updater(current);
    if (next === current) continue;

    if (!nextItems) nextItems = items.slice();
    nextItems[index] = next;
  }

  return nextItems ?? items;
}

function hasMatchingId(
  value: Record<string, unknown>,
  keys: readonly string[],
  tagId: string
): boolean {
  return keys.some((key) => {
    const candidate = value[key];
    return typeof candidate === 'string' && candidate.trim() === tagId;
  });
}

function updateRuntimeLabelFields(
  value: Record<string, unknown>,
  tagLabel: string
): Record<string, unknown> {
  const hasCamelCaseLabel = 'tagLabel' in value;
  const hasSnakeCaseLabel = 'tag_label' in value;
  let next = value;

  if (
    (!hasCamelCaseLabel && !hasSnakeCaseLabel) ||
    (hasCamelCaseLabel &&
      (typeof value.tagLabel !== 'string' || value.tagLabel.trim() !== tagLabel))
  ) {
    next = { ...next, tagLabel };
  }

  if (
    hasSnakeCaseLabel &&
    (typeof value.tag_label !== 'string' || value.tag_label.trim() !== tagLabel)
  ) {
    next = { ...next, tag_label: tagLabel };
  }

  return next;
}

function updateStoryboardBoardItemTagLabel(
  item: StoryboardBoardItem,
  tagId: string,
  tagLabel: string
): StoryboardBoardItem {
  if (!isRecord(item)) return item;

  const topLevelMatches = hasMatchingId(
    item,
    ['tagId', 'elementTagId', 'tag_id', 'element_tag_id'],
    tagId
  );
  const nestedData = isRecord(item.data) ? item.data : null;
  const nestedMatches =
    nestedData !== null &&
    hasMatchingId(nestedData, ['tagId', 'elementTagId', 'tag_id', 'element_tag_id'], tagId);

  let nextItem = item;
  if (topLevelMatches || nestedMatches) {
    const currentTagLabel = typeof item.tagLabel === 'string' ? item.tagLabel.trim() : null;
    if (currentTagLabel !== tagLabel) {
      nextItem = { ...nextItem, tagLabel };
    }
  }

  if (nestedData && nestedMatches) {
    const nextData = updateRuntimeLabelFields(nestedData, tagLabel);
    if (nextData !== nestedData) {
      nextItem = { ...nextItem, data: nextData };
    }
  }

  return nextItem;
}

function updateStoryboardBoardTracks(
  tracks: StoryboardBoardTrackWithItems[],
  tagId: string,
  tagLabel: string
): StoryboardBoardTrackWithItems[] {
  return mapKeepingArrayReference(tracks, (track) => {
    const nextItems = mapKeepingArrayReference(track.items, (item) =>
      updateStoryboardBoardItemTagLabel(item, tagId, tagLabel)
    );
    return nextItems === track.items ? track : { ...track, items: nextItems };
  });
}

function updateStoryboardBoardCache(
  current: StoryboardBoardCacheData | undefined,
  tagId: string,
  tagLabel: string
): StoryboardBoardCacheData | undefined {
  if (!current) return current;
  if (Array.isArray(current)) {
    return updateStoryboardBoardTracks(current, tagId, tagLabel);
  }
  if (!current.tracks) return current;

  const nextTracks = updateStoryboardBoardTracks(current.tracks, tagId, tagLabel);
  return nextTracks === current.tracks ? current : { ...current, tracks: nextTracks };
}

function syncStoryboardBoardTagLabel(
  queryClient: QueryClient,
  tagId: string,
  tagLabel: string
): void {
  const updater = (current: StoryboardBoardCacheData | undefined) =>
    updateStoryboardBoardCache(current, tagId, tagLabel);

  queryClient.setQueriesData<StoryboardBoardCacheData>(
    { queryKey: storyboardBoardKeys.lists() },
    updater
  );
  queryClient.setQueriesData<StoryboardBoardCacheData>(
    { queryKey: storyboardBoardKeys.paginateds() },
    updater
  );
}

function syncStoryboardItemsTagLabel(
  queryClient: QueryClient,
  tagId: string,
  tagLabel: string
): void {
  queryClient.setQueriesData<StoryboardItem[]>({ queryKey: [STORYBOARD_ITEMS_ROOT] }, (current) => {
    if (!current) return current;
    return mapKeepingArrayReference(current, (item) => {
      if (item.elementTagId?.trim() !== tagId) return item;
      const currentTagLabel = item.tagLabel?.trim() ?? null;
      return currentTagLabel === tagLabel ? item : { ...item, tagLabel };
    });
  });
}

function syncStoryboardDialoguesTagLabel(
  queryClient: QueryClient,
  tagId: string,
  tagLabel: string
): void {
  queryClient.setQueriesData<StoryboardDialogueItem[]>(
    { queryKey: storyboardDialoguesKeys.all },
    (current) => {
      if (!current) return current;
      return mapKeepingArrayReference(current, (dialogue) => {
        if (dialogue.characterTagId?.trim() !== tagId) return dialogue;
        const currentTagLabel = dialogue.tagLabel?.trim() ?? null;
        return currentTagLabel === tagLabel ? dialogue : { ...dialogue, tagLabel };
      });
    }
  );
}

function isResourceElementType(elementType: ElementType): elementType is ProjectElementType {
  return elementType === 'character' || elementType === 'location' || elementType === 'prop';
}

/**
 * 标签更新接口成功后先同步当前内存中的派生缓存，再由调用方执行后台失效校准。
 * 仅修改标签名称字段，其他快照字段保持不变。
 */
export function syncProjectElementTagDependentCaches(
  queryClient: QueryClient,
  result: ProjectElementTagResponse
): void {
  if (!isResourceElementType(result.elementType)) return;

  const tagId = result.tagId.trim();
  const tagLabel = result.tagLabel.trim();

  syncProjectElementTagToResourceLibraryCaches(queryClient, {
    elementType: result.elementType,
    elementId: result.elementId,
    tagId,
    tagLabel,
  });
  syncStoryboardBoardTagLabel(queryClient, tagId, tagLabel);
  syncStoryboardItemsTagLabel(queryClient, tagId, tagLabel);
  syncMentionTagLabelInCache(queryClient, { tagId, tagLabel });

  if (result.elementType === 'character') {
    syncStoryboardDialoguesTagLabel(queryClient, tagId, tagLabel);
  }
}
